<template>
  <div v-if="ready" class="add-edit-container">
    <div
      class="d-flex bg-extra-light-gray justify-content-between align-items-center px-4 py-3"
    >
      <h5 class="mb-0">{{ formName }}</h5>
      <div class="text-white">
        <b-button
          class="button-custom-blue mr-2"
          style="height: 31px"
          variant="primary"
          @click="saveFormAndBack"
          ><i class="fa fa-save mr-2" aria-hidden="true"></i>Save Form</b-button
        >
        <b-dropdown
          @show="checkLayoutClipboard"
          variant="primary"
          right
          toggle-class=""
          size="sm"
        >
          <template #button-content>
            <i class="fa fa-cog mr-2" aria-hidden="true"></i> Options
          </template>
          <b-dropdown-item-button @click="showFormPreview">
            <i class="fa fa-file-import mr-2" style="width: 16px"></i>Preview
          </b-dropdown-item-button>
          <b-dropdown-item-button
            v-if="versions.length > 1"
            @click="changeDisplayVersions(true)"
            ><i class="fa fa-clock mr-2" style="width: 16px"></i>History
          </b-dropdown-item-button>
          <b-dropdown-divider></b-dropdown-divider>
          <b-dropdown-item-button
            @click="copyLayoutToClipboard({ formId: form.id })"
          >
            <i class="far fa-copy mr-2" style="width: 16px"></i>Copy layout to
            clipboard
          </b-dropdown-item-button>
          <b-dropdown-item-button
            :disabled="!hasCorrectLayoutFormat"
            @click="pasteLayoutFromClipboard({ formId: form.id })"
          >
            <i class="far fa-clipboard mr-2" style="width: 16px"></i>Paste from
            clipboard
          </b-dropdown-item-button>
        </b-dropdown>
      </div>
    </div>
    <div
      class="form-builder h-100 d-flex justify-content-between position-relative"
    >
      <fb-form-element-edit :formId="form.id"></fb-form-element-edit>
      <div class="panel-side bg-extra-med-light-grey">
        <div class="m-2">
          <b-form-group
            description=""
            label=""
            class="text-left sidebar-component"
          >
            <b-form-input
              size="sm"
              placeholder="Search components..."
              v-model="search"
            ></b-form-input>
          </b-form-group>
          <div class="overflow-auto" style="max-height: calc(100vh - 290px)">
            <drag
              class="drag sidebar-component sidebar-component-element bg-light-cyan mb-1"
              :key="'basic-' + componentIndex"
              v-for="(component, componentIndex) in getBasicElementComponents"
              :transfer-data="getInitialElementJson(component.elType)"
              :effect-allowed="['copy']"
              drop-effect="copy"
              @dragstart="
                setFormBuilderDragging({
                  dragging: true,
                  formId: form.id,
                  elType: component.elType
                })
              "
              @dragend="
                setFormBuilderDragging({
                  dragging: false,
                  formId: form.id,
                  elType: null
                })
              "
              :hideImageHtml="false"
            >
              <template slot="image">
                <div
                  class="d-flex justify-content-between align-items-center p-1 border border-light-grey rounded w-100"
                >
                  <div>
                    <b-icon-grip-horizontal />
                    {{ component.label }}
                  </div>
                  <b-icon :icon="component.icon"></b-icon>
                </div>
              </template>
            </drag>
            <hr v-if="showAdvancedComponents" />
            <drag
              class="drag sidebar-component sidebar-component-element bg-light-cyan mb-1"
              :key="'advanced-' + componentIndex"
              v-for="(
                component, componentIndex
              ) in getAdvancedElementComponents"
              :transfer-data="getInitialElementJson(component.elType)"
              :effect-allowed="['copy']"
              drop-effect="copy"
              @dragstart="
                setFormBuilderDragging({
                  dragging: true,
                  formId: form.id,
                  elType: component.elType
                })
              "
              @dragend="
                setFormBuilderDragging({
                  dragging: false,
                  formId: form.id,
                  elType: null
                })
              "
              :hideImageHtml="false"
            >
              <template slot="image">
                <div
                  class="d-flex justify-content-between align-items-center p-1 border border-light-grey rounded w-100"
                >
                  <div>
                    <b-icon-grip-horizontal />
                    <span>{{ component.label }}</span>
                    <span v-if="component.eFormHidden" class="text-danger ml-1"
                      >*</span
                    >
                  </div>

                  <b-icon :icon="component.icon"></b-icon>
                </div>
              </template>
            </drag>
            <div
              v-if="category != 'eocAdditionalInfo'"
              class="text-center show-advanced-components"
            >
              <small
                @click="showAdvancedComponents = !showAdvancedComponents"
                class="hover-pointer text-primary"
                >{{
                  !showAdvancedComponents
                    ? "Show Advanced Components"
                    : "Hide Advanced Components"
                }}</small
              >
            </div>
          </div>
        </div>
      </div>
      <div
        class="builder-main-area h-100 w-100 text-center overflow-auto p-3 bg-white"
      >
        <fb-form-builder
          :json="json.layout"
          :formId="form.id"
          @update-json="updateJsonLayout"
        ></fb-form-builder>
      </div>
      <div class="panel-side bg-extra-med-light-grey overflow-auto">
        <div class="h-100 d-flex flex-column justify-content-between">
          <div class="p-2 form-settings overflow-auto">
            <b-form-group label="Form Name" class="sidebar-form-component">
              <b-input v-model="formName" size="sm" maxLength="100" />
            </b-form-group>
            <b-form-group label="Modal Size" class="sidebar-form-component">
              <b-select size="sm" v-model="modalSize" :options="modalSizes" />
            </b-form-group>
            <template v-if="category == 'main'">
              <b-form-group label="Folder" class="sidebar-form-component">
                <b-select size="sm" v-model="formMenu" :options="menuOptions" />
              </b-form-group>
              <b-form-group
                label="Document instructions"
                class="sidebar-form-component"
              >
                <smart-text-input
                  v-if="showing"
                  v-model="documentInstructions"
                  :enableMentions="false"
                  :showSubmitButton="false"
                  backgroundColor="#FFF"
                  placeholder=" "
                  :showFullToolBar="true"
                />
              </b-form-group>
              <b-form-group class="sidebar-form-component">
                <b-form-checkbox v-model="showInOmt">
                  Show in OMT
                </b-form-checkbox>
              </b-form-group>
            </template>
            <b-form-group
              v-if="category == 'masterTreatmentPlan'"
              label="Map Autocomplete Function Body"
              class="sidebar-form-component"
            >
              <form-builder-code-editor
                v-model="formMapAutocompleteFunctionBody"
              />
            </b-form-group>

            <b-form-group
              v-if="category == 'masterTreatmentPlan'"
              label="Treatment Plan Objectives Map"
              class="sidebar-form-component"
            >
              <b-form-textarea
                size="sm"
                class="v-select-sm"
                v-model="formTreatmentPlanObjectivesMap"
                rows="20"
              ></b-form-textarea>
            </b-form-group>
            <b-form-group
              v-if="this.advancedOptions"
              label="Score Function Body"
              class="sidebar-form-component"
            >
              <b-form-textarea
                size="sm"
                class="v-select-sm"
                v-model="formScoreFunctionBody"
                rows="20"
              />
            </b-form-group>
            <b-form-group
              v-if="category == 'main'"
              label="Values in the document list"
              class="sidebar-form-component"
            >
              <div
                class="component-select-container overflow-auto bg-white p-2"
              >
                <b-form-checkbox-group
                  v-model="gridComponents"
                  stacked
                  class="lower-font-size"
                >
                  <div class="d-flex">
                    <div
                      class="min-width-50"
                      v-for="(chunk, chunkIndex) in componentOptions"
                      :key="chunkIndex"
                    >
                      <b-form-checkbox
                        v-for="option in chunk"
                        :key="option.value"
                        :value="option.value"
                        >{{ option.text }}</b-form-checkbox
                      >
                    </div>
                  </div>
                </b-form-checkbox-group>
              </div>
            </b-form-group>
          </div>
        </div>
      </div>
    </div>
    <b-modal
      centered
      ref="form-modal"
      title="History"
      header-bg-variant="primary"
      header-text-variant="white"
      hide-footer
      no-close-on-backdrop
      size="md"
      v-model="showVersions"
      @ok="showVersions = false"
      modal-class="fb-history-modal"
      header-class="bg-purple"
    >
      <div
        class="d-flex flex-column align-items-start justify-content-between mb-4"
      >
        <ol class="pl-1 w-100">
          <li
            :key="versionIndex"
            v-for="(version, versionIndex) in versions"
            class="d-flex flex-row justify-content-between mb-2"
            :class="versionIndex > 0 ? 'border-top pt-2' : ''"
          >
            <div class="d-flex flex-row justify-content-start">
              <div class="mr-2">{{ versionIndex + 1 }}.</div>
              <div>
                <div
                  :class="officialVersionId != version.id ? 'color-purple' : ''"
                >
                  {{ formatDate(version.createdAt) }}
                </div>
                <div class="user-name">
                  {{ version.createdBy ? version.createdBy.name : "" }}
                </div>
              </div>
            </div>
            <div class="d-flex flex-row justify-content-end align-items-start">
              <b-button
                v-if="officialVersionId != version.id"
                @click="setVersionOfficial(version.id)"
                class=""
                variant="primary"
                size="sm"
              >
                Restore Version
              </b-button>
              <span v-else class="current-version">Current Version</span>
            </div>
          </li>
        </ol>
      </div>
    </b-modal>
    <form-builder-preview-modal
      v-if="showPreview"
      v-model="showPreview"
      @onModify="onModifyFormPreview"
      :json="json"
      :modalTitle="formName"
      :modalSize="modalSize"
      :form="form"
    />
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from "vuex";
import {
  getElementComponents,
  getElementComponentLastVersion,
  getElementComponentData
} from "../../helpers/formBuilder";
import { randomId, chunkColumns } from "../../helpers/general";
import FormBuilderPreviewModal from "./FormBuilderPreviewModal";
import FormBuilderCodeEditor from "./FormBuilderCodeEditor";
import SmartTextInput from "../controls/SmartTextInput.vue";
import formServer from "../../server/form";

export default {
  name: "FormBuilderAddEdit",
  components: {
    FormBuilderPreviewModal,
    FormBuilderCodeEditor,
    SmartTextInput
  },
  props: ["showing", "id", "category"],
  data() {
    return {
      formId: this.id,
      form: null,
      search: "",
      showVersions: false,
      ready: false,
      showAdvancedComponents: false,
      showPreview: false,
      files: [],
      lastTimeEditing: null,
      hasCorrectLayoutFormat: false
    };
  },
  created() {
    if (this.id) {
      this.lastTimeEditing = setInterval(async () => {
        await formServer.updateLastTimeEditing(this.id);
      }, 5000);
    }
  },
  destroyed() {
    clearInterval(this.lastTimeEditing);
  },
  async mounted() {
    this.form = {
      id: randomId(),
      isNew: true
    };
    let versions = null;
    if (this.formId) {
      this.form = await this.getFormBuilderFormById(this.formId);
      versions = await this.getFormBuilderFormVersionsByFormId(this.formId);
    }

    await this.fetchFormBuilderMenus();
    const defaultMenu = this.menus.find((m) => !m.parentId);
    setTimeout(() => {
      if (!this.formMenu) this.formMenu = defaultMenu.id;
    }, 500);

    this.initializeFormBuilderFormJson({
      form: this.form,
      versions,
      category: this.category
    });
    this.ready = true;
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError"]),
    ...mapMutations("formBuilder", [
      "setFormBuilderFormJson",
      "initializeFormBuilderFormJson",
      "setFormBuilderFormData",
      "setFormBuilderVersionSelected",
      "setFormBuilderVersionOfficial",
      "setFormBuilderDragging"
    ]),
    ...mapActions("formBuilder", [
      "addFormBuilderForm",
      "editFormBuilderForm",
      "addFormBuilderFormVersion",
      "editFormBuilderFormVersion",
      "fetchFormBuilderMenus",
      "fetchFormBuilderForms",
      "addFormBuilderFormToContext",
      "copyLayoutToClipboard",
      "pasteLayoutFromClipboard",
      "updateDependencies"
    ]),
    ...mapMutations("client", ["dispatchListener"]),
    updateJsonLayout(newJsonLayout) {
      this.setFormBuilderFormJson({
        newJson: {
          ...this.json,
          layout: newJsonLayout
        },
        formId: this.form.id
      });
    },
    async saveFormAndBack() {
      await this.saveForm();
    },
    async saveForm() {
      let noErrors = true;
      if (this.formId) {
        await this.editFormBuilderForm({ formId: this.form.id }).catch((e) => {
          noErrors = false;
          if (e.response.data.errorCode == "formNameAlreadyExists") {
            this.showError({
              message: "A form with this name already exists"
            });
          }
        });
      } else {
        const self = this;
        await this.addFormBuilderForm({ formId: this.form.id })
          .then((newForm) => {
            self.formId = newForm.id;
            this.addFormBuilderFormToContext({ formId: this.form.id, newForm });
          })
          .catch((e) => {
            noErrors = false;
            if (e.response.data.errorCode == "formNameAlreadyExists") {
              this.showError({
                message: "A form with this name already exists"
              });
            }
          });
      }
      if (!noErrors) return;

      if (this.selectedVersionId == -1) {
        await this.addFormBuilderFormVersion({ formId: this.form.id });
      } else {
        await this.editFormBuilderFormVersion({ formId: this.form.id }).catch(
          (e) => {
            noErrors = false;
            if (e.response.data.errorCode == "cannotUpdateForm") {
              this.showError({
                message:
                  "This form has documents associated, in order to edit please close it and open it again"
              });
            }
          }
        );
      }
      if (!noErrors) return;

      await this.editFormBuilderForm({ formId: this.form.id });
      await this.fetchFormBuilderForms({ category: this.category });
      this.dispatchListener({
        listener: "fetchDocumentMenus",
        param: "all"
      });

      this.ready = false;
      this.form = await this.getFormBuilderFormById(this.formId);
      const versions = await this.getFormBuilderFormVersionsByFormId(
        this.formId
      );
      this.initializeFormBuilderFormJson({
        form: this.form,
        versions,
        category: this.category
      });

      this.showAlertSuccess();
      this.$emit("update-current-tab", {
        key: "form-builder-form-" + this.form.id,
        text: this.formName,
        props: { id: this.form.id, category: this.category }
      });
      this.ready = true;
    },
    setVersionOfficial(versionId) {
      if (this.jsonChanged || this.metadataChanged) {
        this.$dialog
          .confirm("Do you want to save current version")
          .then(async () => {
            await this.saveFormAndBack();
            this.setFormBuilderVersionSelected({
              versionId,
              formId: this.form.id
            });
            this.setFormBuilderVersionOfficial({
              versionId,
              formId: this.form.id
            });
            this.changeDisplayVersions(false);
          })
          .catch(() => {
            this.setFormBuilderVersionSelected({
              versionId,
              formId: this.form.id
            });
            this.setFormBuilderVersionOfficial({
              versionId,
              formId: this.form.id
            });
            this.changeDisplayVersions(false);
          });
      } else {
        this.setFormBuilderVersionSelected({ versionId, formId: this.form.id });
        this.setFormBuilderVersionOfficial({ versionId, formId: this.form.id });
        this.changeDisplayVersions(false);
      }
    },
    getInitialElementJson(jsonElementType) {
      const elementVersion = getElementComponentLastVersion(jsonElementType);
      const elementData = getElementComponentData(
        jsonElementType,
        elementVersion
      );
      return {
        elType: jsonElementType,
        version: elementVersion,
        id: this.getElementUniqueId({
          elementType: jsonElementType,
          formId: this.form.id
        }),
        page: 0,
        ...elementData.defaultAttrs
      };
    },
    changeDisplayVersions(showVersions) {
      this.showVersions = showVersions;
    },
    showFormPreview() {
      this.updateDependencies({ formId: this.form.id });
      this.showPreview = true;
    },
    onModifyFormPreview() {
      this.showPreview = false;
    },
    checkLayoutClipboard() {
      navigator.clipboard.readText().then((clipText) => {
        this.hasCorrectLayoutFormat = clipText.startsWith('{"rows":');
      });
    }
  },
  computed: {
    ...mapState("formBuilder", {
      forms: (state) => state.form,
      menus: (state) => state.menus
    }),
    ...mapState("layout", {
      advancedOptions: (state) => state.advancedOptions
    }),
    json() {
      return this.form ? this.forms[this.form.id].json : "";
    },
    jsonChanged() {
      return this.form ? this.forms[this.form.id].jsonChanged : "";
    },
    metadataChanged() {
      return this.form ? this.forms[this.form.id].metadataChanged : "";
    },
    versions() {
      return this.form ? this.forms[this.form.id].versions : "";
    },
    selectedVersionId() {
      return this.form ? this.forms[this.form.id].selectedVersionId : "";
    },
    officialVersionId() {
      return this.form ? this.forms[this.form.id].officialVersionId : "";
    },
    ...mapGetters("formBuilder", [
      "getFormBuilderFormById",
      "getFormBuilderFormVersionsByFormId",
      "getFormBuilderComponents",
      "getElementUniqueId"
    ]),
    formName: {
      get() {
        return this.$store.state.formBuilder.form[this.form.id].name;
      },
      set(newFormName) {
        this.setFormBuilderFormData({
          data: { name: newFormName },
          formId: this.form.id
        });
      }
    },
    documentInstructions: {
      get() {
        return this.$store.state.formBuilder.form[this.form.id]
          .documentInstructions;
      },
      set(newValue) {
        this.setFormBuilderFormData({
          data: { documentInstructions: newValue },
          formId: this.form.id
        });
      }
    },
    showInOmt: {
      get() {
        return this.$store.state.formBuilder.form[this.form.id].showInOmt;
      },
      set(newValue) {
        this.setFormBuilderFormData({
          data: { showInOmt: newValue },
          formId: this.form.id
        });
      }
    },
    getBasicElementComponents() {
      return getElementComponents().filter(
        (component) =>
          (this.search == "" ||
            component.label.toLowerCase().indexOf(this.search.toLowerCase()) >
              -1) &&
          !component.listHidden &&
          !component.advanced &&
          (this.category != "eocAdditionalInfo" || component.additionalInfo)
      );
    },
    getAdvancedElementComponents() {
      return getElementComponents().filter(
        (component) =>
          (this.search == "" ||
            component.label.toLowerCase().indexOf(this.search.toLowerCase()) >
              -1) &&
          !component.listHidden &&
          component.advanced &&
          this.showAdvancedComponents &&
          (this.advancedOptions || component.alwaysVisible)
      );
    },
    gridComponents: {
      get() {
        return this.json.grid.components;
      },
      set(newValue) {
        this.setFormBuilderFormJson({
          newJson: {
            ...this.json,
            grid: {
              components: [...newValue]
            }
          },
          formId: this.form.id
        });
      }
    },
    componentOptions() {
      return chunkColumns(
        this.getFormBuilderComponents({ formId: this.form.id })
          .sort((c1, c2) => c1.id.localeCompare(c2.id))
          .map((component) => ({
            value: component.id,
            text: component.id
          })),
        2
      );
    },
    formMenu: {
      get() {
        return this.$store.state.formBuilder.form[this.form.id].menu;
      },
      set(newFormMenu) {
        this.setFormBuilderFormData({
          data: { menu: newFormMenu },
          formId: this.form.id
        });
      }
    },
    menuOptions() {
      return this.menus.map((menu) => ({
        value: menu.id,
        text: menu.name
      }));
    },
    modalSize: {
      get() {
        return this.$store.state.formBuilder.form[this.form.id].json.properties
          .modalSize;
      },
      set(newModalSize) {
        this.setFormBuilderFormJson({
          newJson: {
            ...this.$store.state.formBuilder.form[this.form.id].json,
            properties: {
              ...this.$store.state.formBuilder.form[this.form.id].json
                .properties,
              modalSize: newModalSize
            }
          },
          formId: this.form.id
        });
      }
    },
    modalSizes() {
      return [
        {
          value: "lg",
          text: "Medium"
        },
        {
          value: "xl",
          text: "Large"
        }
      ];
    },
    formMapAutocompleteFunctionBody: {
      get() {
        return this.json.properties.mapAutocompleteFunctionBody;
      },
      set(newValue) {
        this.setFormBuilderFormJson({
          newJson: {
            ...this.json,
            properties: {
              ...this.json.properties,
              mapAutocompleteFunctionBody: newValue
            }
          },
          formId: this.form.id
        });
      }
    },
    formTreatmentPlanObjectivesMap: {
      get() {
        return this.json.properties.treatmentPlanObjectivesMap;
      },
      set(newValue) {
        this.setFormBuilderFormJson({
          newJson: {
            ...this.json,
            properties: {
              ...this.json.properties,
              treatmentPlanObjectivesMap: newValue
            }
          },
          formId: this.form.id
        });
      }
    },
    formScoreFunctionBody: {
      get() {
        return this.json.properties.scoreFunctionBody;
      },
      set(newValue) {
        this.setFormBuilderFormJson({
          newJson: {
            ...this.json,
            properties: {
              ...this.json.properties,
              scoreFunctionBody: newValue
            }
          },
          formId: this.form.id
        });
      }
    }
  }
};
</script>
<style scoped>
.panel-side {
  width: 350px;
  min-width: 350px;
  position: relative;
}
.drag {
  font-size: 0.8rem;
}
.go-back:hover {
  cursor: pointer;
}
.border-wider {
  border: 10px solid black;
}
.add-edit-container {
  height: calc(100vh - 240px);
}
.form-name {
  background-color: #b5b5b5 !important;
  border-radius: 0 !important;
  border: none !important;
  height: 25px;
  color: white !important;
}
.versions-container {
  min-height: 50%;
  background-color: white;
  font-size: 0.8rem;
}
.versions-container .btn {
  font-size: 0.8rem !important;
}
.form-settings {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
.user-name {
  color: #e0e0e0;
}
.current-version {
  background-color: #a3a3a3;
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.2rem;
  color: white;
}
.form-revision:hover {
  cursor: pointer;
}
.pencil-icon {
  margin-top: 2px;
  margin-right: 2px;
}
.sidebar-component {
  margin: 9px 7px;
}
.sidebar-form-component {
  margin: 0px 7px 18px 7px;
}
.component-select-container {
  /* min-height: calc(100vh - 470px);
  max-height: calc(100vh - 470px); */
  margin-bottom: 0.5rem;
}
.lower-font-size {
  font-size: 14px !important;
}
.min-width-50 {
  min-width: 50%;
}
.builder-main-area {
  box-shadow: inset 0px 0px 6px 0px rgb(0 0 0 / 50%);
}
.sidebar-component-element:hover {
  cursor: move;
}
.show-advanced-components {
  margin: 20px 7px;
}
</style>

<style>
.lower-font-size .custom-control-label {
  padding-top: 2px;
}
.fb-history-modal .modal-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
</style>
